/**
 * phoenix-customizer.js
 * Core logic for the Phoenix Putter Customizer using Fabric.js
 */

document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('phoenix-customizer-app');
    if (!container) return; // Not on a customizer page

    // Configuration from Shopify injected via data attributes
    const config = {
        basePrice: parseFloat(container.dataset.basePrice || 0),
        facePrice: parseFloat(container.dataset.facePrice || 20),
        backPrice: parseFloat(container.dataset.backPrice || 30),
        solePrice: parseFloat(container.dataset.solePrice || 15),
        currency: container.dataset.currency || '$',
        productTitle: container.dataset.productTitle || 'Custom Putter',
        variantId: container.dataset.variantId || '',
        assets: JSON.parse(container.dataset.assets || '{}'),
        debug: container.dataset.debug === 'true' // Toggle via section settings if needed
    };

    // Cloudinary unsigned upload — set your Cloud Name and Preset here
    const CLOUDINARY_CLOUD_NAME = 'dtnr0k2yp';
    const CLOUDINARY_UPLOAD_PRESET = 'phoenix_putters';    // unsigned preset name

    async function uploadToCloudinary(file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        fd.append('folder', 'phoenix-orders');

        const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
            method: 'POST',
            body: fd
        });

        if (!res.ok) {
            const errBody = await res.text();
            throw new Error(`Image upload failed: ${errBody}`);
        }

        const data = await res.json();
        return data.secure_url;
    }

    // Application State
    const state = {
        step: 1,
        isFabricReady: false,
        baseOptions: {
            length: '34"',
            handedness: 'Right Hand',
            grip: document.getElementById('px-grip-select') ? document.getElementById('px-grip-select').value : '',
            faceMilling: 'Deep Mill'
        },
        activeZone: 'face', // 'face', 'back', 'sole'
        zones: {
            face: { customized: false },
            back: { customized: false },
            sole: { customized: false }
        },
        customization: null,
        pendingFiles: {
            face: [],
            back: [],
            sole: []
        }
    };

    // Fabric Canvas Instances
    const canvases = {};
    const CANVAS_SIZE = 800; // Fixed high-res internal size

    // DOM Elements
    const els = {
        steps: document.querySelectorAll('.px-step-group'),
        priceDisplays: document.querySelectorAll('.px-live-price'),
        zoneCards: document.querySelectorAll('.px-zone-card'),
        canvasContainer: document.getElementById('px-canvas-wrapper'),
        tabs: document.querySelectorAll('.px-tab'),
        tabContents: document.querySelectorAll('.px-tab-content'),
        thumbButtons: document.querySelectorAll('.px-thumb'),
        objToolbar: document.getElementById('px-object-toolbar'),
        progressBar: document.querySelector('.px-progress-bar'),
        stepperIndicators: document.querySelectorAll('.px-step-indicator'),
        addTextInput: document.getElementById('px-add-text-input'),
        addTextBtn: document.getElementById('px-add-text-btn'),
        fontSelect: document.getElementById('px-font-select'),
        deleteBtn: document.getElementById('px-delete-obj-btn'),
        addToCartBtn: document.getElementById('px-add-to-cart-btn'),
        imageUpload: document.getElementById('px-image-upload'),
        choiceInputs: document.querySelectorAll('.px-choice-input'),
        choiceFieldset: document.querySelector('.px-choice'),
        choiceError: document.querySelector('.px-choice-error')
    };

    /**
     * Initialize Application
     */
    function init() {
        if (config.debug) console.log('Phoenix Customizer: Initializing...', config);
        try {
            setupCanvases();
            bindEvents();
            updatePrice();
            
            // Initial view setup
            if (canvases['face']) {
                switchZone('face');
            } else {
                const firstZone = Object.keys(canvases)[0];
                if (firstZone) switchZone(firstZone);
            }
        } catch (err) {
            console.error('Phoenix Customizer: Initialization failed', err);
        }
    }

    /**
     * Setup Fabric.js Canvases for each zone
     */
    function setupCanvases() {
        ['face', 'back', 'sole'].forEach(zone => {
            // Create canvas element dynamically
            const canvasEl = document.createElement('canvas');
            canvasEl.id = `px-canvas-${zone}`;
            els.canvasContainer.appendChild(canvasEl);

            // Initialize Fabric
            const fCanvas = new fabric.Canvas(canvasEl.id, {
                width: CANVAS_SIZE,
                height: CANVAS_SIZE,
                preserveObjectStacking: true,
                backgroundColor: 'transparent'
            });

            canvases[zone] = fCanvas;

            // Hide non-active canvases
            fCanvas.wrapperEl.style.display = 'none';

            // Load Base Image & Mask
            const assets = config.assets[zone];
            if (assets && assets.view) {
                els.canvasContainer.classList.add('loading');

                fabric.Image.fromURL(assets.view, (baseImg) => {
                    if (!baseImg) {
                        els.canvasContainer.classList.remove('loading');
                        return;
                    }

                    baseImg.set({
                        originX: 'center', originY: 'center',
                        left: CANVAS_SIZE / 2, top: CANVAS_SIZE / 2,
                        selectable: false, evented: false
                    });
                    // Scale to fit
                    const scale = Math.min(CANVAS_SIZE / baseImg.width, CANVAS_SIZE / baseImg.height);
                    baseImg.scale(scale);
                    fCanvas.add(baseImg);
                    fCanvas.sendToBack(baseImg);

                    // Add Clipping Mask if available
                    if (assets.mask) {
                        fabric.Image.fromURL(assets.mask, (maskImg) => {
                            if (maskImg) {
                                maskImg.set({
                                    originX: 'center', originY: 'center',
                                    left: CANVAS_SIZE / 2, top: CANVAS_SIZE / 2,
                                    selectable: false, evented: false,
                                    opacity: 0 // Invisible mask for calculation
                                });
                                maskImg.scale(scale);
                                fCanvas.clipPath = maskImg;
                            }
                            els.canvasContainer.classList.remove('loading');
                            fCanvas.renderAll();
                        }, { crossOrigin: 'anonymous' });
                    } else {
                        els.canvasContainer.classList.remove('loading');
                        fCanvas.renderAll();
                    }
                }, { crossOrigin: 'anonymous' });
            }

            // Event listeners for Object selection
            fCanvas.on('selection:created', handleObjectSelected);
            fCanvas.on('selection:updated', handleObjectSelected);
            fCanvas.on('selection:cleared', handleObjectCleared);
            fCanvas.on('object:modified', () => markZoneCustomized(zone));
            fCanvas.on('object:added', (e) => {
                if (e.target && e.target.selectable) markZoneCustomized(zone);
            });
        });

        // Re-render canvases once web fonts arrive — Fabric text is drawn to
        // canvas pixels at creation time, so without this any text added
        // before the Google Font finishes loading stays in the fallback face.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
                Object.values(canvases).forEach(c => {
                    c.getObjects().forEach(obj => {
                        if (obj.type === 'textbox' || obj.type === 'text' || obj.type === 'i-text') {
                            if (typeof obj.initDimensions === 'function') obj.initDimensions();
                            obj.setCoords();
                        }
                    });
                    c.requestRenderAll();
                });
            });
        }
    }

    /**
     * Bind DOM Events
     */
    function bindEvents() {
        // Step Navigation
        container.addEventListener('click', (e) => {
            const nextBtn = e.target.closest('[data-next-step]');
            const prevBtn = e.target.closest('[data-prev-step]');
            
            if (nextBtn) {
                const target = parseInt(nextBtn.dataset.nextStep);
                // A customization choice is mandatory before leaving step 1
                if (state.step === 1 && !state.customization) {
                    showChoiceError();
                    return;
                }
                // Opted out — skip the design steps entirely
                if (state.customization === 'no' && target === 2) {
                    goToStep(4);
                    return;
                }
                goToStep(target);
            }
            if (prevBtn) {
                let target = parseInt(prevBtn.dataset.prevStep);
                if (state.customization === 'no' && target === 2) target = 1;
                goToStep(target);
            }
        });

        // Zone Selection
        els.zoneCards.forEach(card => {
            card.addEventListener('click', () => {
                switchZone(card.dataset.zone);
                goToStep(3); // Jump to editor
            });
        });

        // Thumbnail / Multiview
        els.thumbButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                if (btn.dataset.view !== 'angled') {
                    switchZone(btn.dataset.view);
                    if (state.step !== 3) goToStep(3);
                } else {
                    // Show 3D/Angled static view overlay logic here
                    alert("3/4 Angled Preview coming soon.");
                }
            });
        });

        // UI Tabs
        els.tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                els.tabs.forEach(t => t.classList.remove('active'));
                els.tabContents.forEach(c => c.classList.remove('active'));
                tab.classList.add('active');
                document.getElementById(`tab-${tab.dataset.target}`).classList.add('active');
            });
        });

        // Add Text
        if (els.addTextBtn) {
            els.addTextBtn.addEventListener('click', () => {
                const text = els.addTextInput.value || 'YOUR TEXT';
                addTextToCanvas(text);
            });
        }

        // Change Font
        if (els.fontSelect) {
            const customFontWrapper = document.getElementById('px-custom-font-wrapper');
            const customFontInput = document.getElementById('px-custom-font-input');

            els.fontSelect.addEventListener('change', (e) => {
                const isOther = e.target.value === 'Other';
                if (customFontWrapper) {
                    customFontWrapper.style.display = isOther ? 'block' : 'none';
                }

                const activeObj = canvases[state.activeZone].getActiveObject();
                if (activeObj && activeObj.type === 'textbox') {
                    const fontToUse = isOther ? (customFontInput.value || 'Outfit') : e.target.value;
                    activeObj.set('fontFamily', fontToUse);
                    canvases[state.activeZone].renderAll();
                }
            });

            if (customFontInput) {
                customFontInput.addEventListener('input', (e) => {
                    const activeObj = canvases[state.activeZone].getActiveObject();
                    if (activeObj && activeObj.type === 'textbox' && els.fontSelect.value === 'Other') {
                        activeObj.set('fontFamily', e.target.value || 'Outfit');
                        canvases[state.activeZone].renderAll();
                    }
                });
            }
        }

        // Color picker logic removed per user request

        // Delete Object
        if (els.deleteBtn) {
            els.deleteBtn.addEventListener('click', () => {
                const canvas = canvases[state.activeZone];
                const activeObj = canvas.getActiveObject();
                if (activeObj) {
                    canvas.remove(activeObj);
                    canvas.discardActiveObject();
                    handleObjectCleared();
                    // Check if zone is empty
                    checkZoneEmpty();
                }
            });
        }

        // Add Graphic (File Upload → Cloudinary CDN)
        if (els.imageUpload) {
            els.imageUpload.addEventListener('change', async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (file.size > 5 * 1024 * 1024) {
                    alert('File must be under 5MB.');
                    return;
                }

                const uploadLabel = document.querySelector('label[for="px-image-upload"]');
                const originalLabelHTML = uploadLabel ? uploadLabel.innerHTML : '';
                if (uploadLabel) {
                    uploadLabel.innerHTML = '<span class="px-loader-sm"></span> Uploading graphic...';
                    uploadLabel.style.pointerEvents = 'none';
                }

                try {
                    const cdnUrl = await uploadToCloudinary(file);

                    const reader = new FileReader();
                    reader.onload = (f) => {
                        addImageToCanvas(f.target.result, cdnUrl);
                    };
                    reader.readAsDataURL(file);
                } catch (err) {
                    console.error('Cloudinary upload failed:', err);
                    alert('Graphic upload failed. Please try again.');
                } finally {
                    if (uploadLabel) {
                        uploadLabel.innerHTML = originalLabelHTML;
                        uploadLabel.style.pointerEvents = '';
                    }
                    els.imageUpload.value = '';
                }
            });
        }

        // Responsiveness: Update canvas offset on resize
        window.addEventListener('resize', () => {
            Object.values(canvases).forEach(c => c.calcOffset());
        });

        // Add to Cart
        if (els.addToCartBtn) {
            els.addToCartBtn.addEventListener('click', handleAddToCart);
        }

        // Customization opt-in / opt-out
        els.choiceInputs.forEach(input => {
            input.addEventListener('change', () => {
                if (!input.checked) return;
                state.customization = input.value;
                if (els.choiceError) els.choiceError.hidden = true;
                if (els.choiceFieldset) els.choiceFieldset.classList.remove('px-choice--invalid');
                container.classList.toggle('px-opted-out', input.value === 'no');

                // Clear any work already done so an opted-out order is truly blank
                if (input.value === 'no') clearAllZones();

                const nextBtn = document.querySelector('[data-next-step="2"]');
                if (nextBtn) {
                    nextBtn.innerHTML = input.value === 'no'
                        ? 'Next: Review &amp; Add to Cart <span class="px-btn-icon">&rarr;</span>'
                        : 'Next: Customize Areas <span class="px-btn-icon">&rarr;</span>';
                }
            });
        });

        const preCheckedChoice = document.querySelector('.px-choice-input:checked');
        if (preCheckedChoice) preCheckedChoice.dispatchEvent(new Event('change'));

        // Base Options Update
        document.querySelectorAll('.px-base-select').forEach(select => {
            select.addEventListener('change', (e) => {
                state.baseOptions[e.target.name] = e.target.value;
                if (e.target.name === 'grip') {
                    updatePrice();
                }
            });
        });
    }

    /**
     * Navigation 
     */
    function goToStep(stepNum) {
        state.step = stepNum;
        els.steps.forEach(s => s.classList.remove('active'));
        const targetStep = document.getElementById(`px-step-${stepNum}`);
        if (targetStep) targetStep.classList.add('active');

        // Update stepper indicators
        if (els.stepperIndicators.length > 0) {
            els.stepperIndicators.forEach((ind, idx) => {
                const stepIdx = idx + 1;
                ind.classList.remove('active', 'complete');
                if (stepIdx === stepNum) {
                    ind.classList.add('active');
                } else if (stepIdx < stepNum) {
                    ind.classList.add('complete');
                    ind.innerHTML = '✓';
                } else {
                    ind.innerHTML = stepIdx;
                }
            });
        }

        // Update progress bar
        if (els.progressBar) {
            els.progressBar.style.width = `${(stepNum / 4) * 100}%`;
        }

        // Auto-scroll sidebar header into view if needed
        const sidebar = document.querySelector('.px-sidebar');
        if (sidebar) sidebar.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function switchZone(zoneName) {
        state.activeZone = zoneName;

        // Hide all canvases
        Object.keys(canvases).forEach(z => {
            canvases[z].wrapperEl.style.display = 'none';
            if (canvases[z].getActiveObject()) {
                canvases[z].discardActiveObject();
                canvases[z].renderAll();
            }
        });

        // Show active
        canvases[zoneName].wrapperEl.style.display = 'block';
        canvases[zoneName].calcOffset(); // Fix mouse coordinates after display change

        // UI Updates
        els.zoneCards.forEach(c => c.classList.remove('active'));
        const activeCard = document.querySelector(`.px-zone-card[data-zone="${zoneName}"]`);
        if (activeCard) activeCard.classList.add('active');

        els.thumbButtons.forEach(b => b.classList.remove('active'));
        const activeThumb = document.querySelector(`.px-thumb[data-view="${zoneName}"]`);
        if (activeThumb) activeThumb.classList.add('active');

        handleObjectCleared();
    }

    /**
     * Core Canvas Operations
     */
    function addTextToCanvas(text) {
        const canvas = canvases[state.activeZone];
        const brandColor = getComputedStyle(document.documentElement).getPropertyValue('--px-primary').trim() || '#D35400';

        const textObj = new fabric.Textbox(text, {
            left: CANVAS_SIZE / 2,
            top: CANVAS_SIZE / 2,
            originX: 'center', originY: 'center',
            fontFamily: els.fontSelect ? els.fontSelect.value : 'Outfit',
            fontSize: 40,
            fill: '#000000',
            textAlign: 'center',
            transparentCorners: false,
            cornerColor: brandColor,
            cornerStrokeColor: '#ffffff',
            borderColor: brandColor,
            cornerSize: 12,
            padding: 10,
            cornerStyle: 'circle'
        });

        canvas.add(textObj);
        canvas.setActiveObject(textObj);
        canvas.renderAll();
    }

    function addImageToCanvas(url, cdnUrl) {
        const canvas = canvases[state.activeZone];

        fabric.Image.fromURL(url, function (img) {
            img.set({
                left: CANVAS_SIZE / 2,
                top: CANVAS_SIZE / 2,
                originX: 'center', originY: 'center',
            });

            // Default scale down to fit nicely in view
            img.scaleToWidth(150);

            // Theme object UI
            img.set({
                transparentCorners: false,
                cornerColor: '#D35400',
                cornerStrokeColor: '#ffffff',
                borderColor: '#D35400',
                cornerSize: 12,
                cornerStyle: 'circle'
            });

            // Store Cloudinary CDN URL for order properties
            if (cdnUrl) {
                img.set('cdnUrl', cdnUrl);
            }

            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
        }, { crossOrigin: 'anonymous' });
    }

    /**
     * UI Responders
     */
    function handleObjectSelected() {
        if (els.objToolbar) {
            els.objToolbar.classList.add('active');
        }
    }

    function handleObjectCleared() {
        if (els.objToolbar) {
            els.objToolbar.classList.remove('active');
        }
    }

    function showChoiceError() {
        if (els.choiceError) {
            els.choiceError.hidden = false;
            if (els.choiceFieldset) {
                els.choiceFieldset.classList.add('px-choice--invalid');
                els.choiceFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            alert('Please choose whether this putter needs customization.');
        }
    }

    function clearAllZones() {
        Object.keys(canvases).forEach(zone => {
            const canvas = canvases[zone];
            canvas.getObjects().filter(obj => obj.selectable).forEach(obj => canvas.remove(obj));
            canvas.discardActiveObject();
            canvas.renderAll();

            if (state.zones[zone]) state.zones[zone].customized = false;

            const cardEl = document.querySelector(`.px-zone-card[data-zone="${zone}"]`);
            if (cardEl) {
                cardEl.classList.remove('customized');
                const status = cardEl.querySelector('.px-status');
                if (status) status.innerText = '';
            }
        });

        handleObjectCleared();
        updatePrice();
    }

    function markZoneCustomized(zone) {
        if (!state.zones[zone].customized) {
            state.zones[zone].customized = true;
            updatePrice();

            // The 'Customized' label is now handled via CSS classes for better layout control
            const cardEl = document.querySelector(`.px-zone-card[data-zone="${zone}"]`);
            if (cardEl) cardEl.classList.add('customized');
        }
    }

    function checkZoneEmpty() {
        const canvas = canvases[state.activeZone];
        // check if it only has base image
        const objCount = canvas.getObjects().length;
        let hasCustomObjects = false;
        canvas.getObjects().forEach(obj => {
            // base image is unselectable
            if (obj.selectable) hasCustomObjects = true;
        });

        if (!hasCustomObjects) {
            state.zones[state.activeZone].customized = false;
            updatePrice();
            const cardEl = document.querySelector(`.px-zone-card[data-zone="${state.activeZone}"] .px-status`);
            if (cardEl) cardEl.innerText = "";
        }
    }

    function updatePrice() {
        let total = config.basePrice;
        if (state.zones.face.customized) total += config.facePrice;
        if (state.zones.back.customized) total += config.backPrice;
        if (state.zones.sole.customized) total += config.solePrice;

        const gripSelect = document.getElementById('px-grip-select');
        if (gripSelect && gripSelect.selectedIndex >= 0) {
            const selectedOption = gripSelect.options[gripSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.price) {
                total += parseFloat(selectedOption.dataset.price);
            }
        }

        els.priceDisplays.forEach(el => {
            el.innerText = `${config.currency}${total.toFixed(2)}`;
        });
    }

    /**
     * Summarize what's on a canvas zone for the order properties.
     * Returns a compact, human-readable string like: "Text: LUCKY 7 (Outfit), Graphic: 1 image"
     */
    function summarizeZone(zoneName) {
        const canvas = canvases[zoneName];
        if (!canvas || !state.zones[zoneName].customized) return null;

        const parts = [];
        let graphicCount = 0;

        canvas.getObjects().forEach(obj => {
            if (!obj.selectable) return; // Skip base image
            if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
                parts.push(`Text: "${obj.text}" (${obj.fontFamily || 'Default'})`);
            } else if (obj.type === 'image') {
                graphicCount++;
            }
        });

        if (graphicCount > 0) {
            parts.push(`Graphic: ${graphicCount} image${graphicCount > 1 ? 's' : ''} uploaded`);
        }

        return parts.length > 0 ? parts.join(', ') : 'Custom design applied';
    }

    /**
     * Add to Cart Logic — uses compact properties that fit Shopify's limits
     */
    async function handleAddToCart(e) {
        const btn = e.target.closest('button');
        if (!btn) return;

        // Never let an order through without an explicit customization answer
        if (!state.customization) {
            goToStep(1);
            showChoiceError();
            return;
        }

        btn.disabled = true;
        const ogText = btn.innerHTML;
        btn.innerHTML = `<span class="px-loader-sm"></span> Preparing Order...`;

        try {
            // 1. Deselect objects for clean state
            Object.values(canvases).forEach(c => c.discardActiveObject().renderAll());

            // Validate all uploaded images have CDN URLs
            let missingUploads = false;
            Object.keys(canvases).forEach(zone => {
                canvases[zone].getObjects().forEach(obj => {
                    if (obj.selectable && obj.type === 'image' && !obj.get('cdnUrl')) {
                        missingUploads = true;
                    }
                });
            });
            if (missingUploads) {
                alert('Some graphics failed to upload. Please remove them and re-upload.');
                btn.disabled = false;
                btn.innerHTML = ogText;
                return;
            }

            // 2. Build compact properties
            const customizedZones = Object.keys(state.zones)
                .filter(z => state.zones[z].customized);

            const properties = {
                'Customization': state.customization === 'no'
                    ? 'No Customization Needed'
                    : 'Customized — see details below',
                'Length': state.baseOptions.length,
                'Handedness': state.baseOptions.handedness,
                'Customized Zones': customizedZones.join(', ') || 'None'
            };

            // Add per-zone summaries
            customizedZones.forEach(zone => {
                const label = zone.charAt(0).toUpperCase() + zone.slice(1);
                properties[`${label} Design`] = summarizeZone(zone);
            });

            // Handle Font Selection
            if (els.fontSelect && state.customization !== 'no') {
                properties['Font Selection'] = els.fontSelect.value;
                if (els.fontSelect.value === 'Other') {
                    const customFontEl = document.getElementById('px-custom-font-input');
                    properties['Custom Font Request'] = customFontEl?.value || 'Custom Request';
                }
            }

            // Add graphic CDN URLs as string properties
            let fileCount = 1;
            Object.keys(canvases).forEach(zone => {
                canvases[zone].getObjects().forEach(obj => {
                    if (obj.selectable && obj.type === 'image' && obj.get('cdnUrl')) {
                        const zoneLabel = zone.charAt(0).toUpperCase() + zone.slice(1);
                        properties[`${zoneLabel} Graphic ${fileCount}`] = obj.get('cdnUrl');
                        fileCount++;
                    }
                });
            });

            // 3. POST to Shopify Cart
            const gripVariantId = state.baseOptions.grip;
            if (gripVariantId && !isNaN(gripVariantId)) {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        items: [
                            { id: config.variantId, quantity: 1, properties },
                            { id: parseInt(gripVariantId), quantity: 1, properties: { '_bundled_for': 'Custom Origin Putter' } }
                        ]
                    })
                });
                if (!response.ok) throw new Error('Add to cart failed');
            } else {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        items: [{ id: config.variantId, quantity: 1, properties }]
                    })
                });
                if (!response.ok) throw new Error('Add to cart failed');
            }

            btn.innerHTML = `✓ Added to Cart`;
            btn.style.background = '#4CAF50';

            setTimeout(() => {
                window.location.href = '/cart';
            }, 800);

        } catch (err) {
            console.error('Customizer Error:', err);
            btn.innerHTML = `Error — Try Again`;
            btn.disabled = false;
            setTimeout(() => { btn.innerHTML = ogText; }, 3000);
        }
    }

    // Run initial setup
    init();
});
